import { useQuery } from "@tanstack/react-query";
import { WorkspaceConfig } from "types/WorkspaceConfig";
import { downloadConfigFile } from "utils/downloadConfigFile";
import { BlobServiceClient } from "@azure/storage-blob";
import { axiosBeyonClient } from "utils/beyonAuth/beyonAxiosClient";
import { getContextVariables } from "utils/getContextVariables";
import { resolveDynamicVariables } from "utils/resolveDynamicVariables";

export function useWorkspaceBlobClient(workspaceId: string) {

    return useQuery({
        queryKey: ['workspace-blobClient', workspaceId],
        queryFn: async () => {
            const { data } = await axiosBeyonClient.get<Workspace>(`workspace/${workspaceId}`);
            // fetch SAS token
            const { data: { sasUri } } = await axiosBeyonClient.get<SharedAccessSignature>(`workspace/${workspaceId}/config/sas`);
            data.sasUri = sasUri;
            const blobServiceClient = new BlobServiceClient(sasUri);
            return blobServiceClient.getContainerClient(''); // container name is inferred from the sas token
        }
    });
}

export function useWorkspace(workspaceId: string) {

    const workspaceBlobClientQuery = useWorkspaceBlobClient(workspaceId);

    const configFileName = "config.json";

    return useQuery({
        queryKey: ['workspace', workspaceId],
        queryFn: async () => {
            const blobClient = workspaceBlobClientQuery.data;
            const { data } = await axiosBeyonClient.get<Workspace>(`workspace/${workspaceId}`);

            let config = await downloadConfigFile<WorkspaceConfig>(blobClient, configFileName);
            const serializedViewConfig = JSON.stringify(config);
            const variables = getContextVariables();
            config = resolveDynamicVariables<WorkspaceConfig>(serializedViewConfig, variables);

            config.filestoreConfig.apiEndpoint
                = config.filestoreConfig.apiEndpoint
                    .toLocaleLowerCase()
                    .replace("{{filestoreid}}", data.fileStoreId);

            config.filestoreConfig.enableThumbnails = config
                .filestoreConfig
                .apiEndpoint
                .toLocaleLowerCase()
                .startsWith("https://api.beca.digital/filestore/workspace/");

            return {
                ...data,
                config
            }
        },
        enabled: workspaceBlobClientQuery.isSuccess
    });
}

export type Workspace = {
    id: string,
    name: string,
    fileStoreId: string,
    sasUri?: string,
    config?: WorkspaceConfig,
}

type SharedAccessSignature = {
    sasUri: string;
}